import {
  Layout,
  LayoutCanvas,
  LayoutInlineForm,
  LayoutObject,
} from '../../../types/types';
import { applyLayoutObjectDefaults } from '../../../utils/layouts/applyLayoutObjectDefaults';
import { sanitizeCanvasPatch } from '../../../utils/layouts/sanitizeCanvasPatch';
import { createLayoutObjectKey } from '../../../utils/layouts/createLayoutObjectKey';
import {
  LAYOUT_CANVAS_DEFAULTS,
  LAYOUT_FIXED_CANVAS_HEIGHT,
  LAYOUT_FIXED_CANVAS_WIDTH,
} from '../../../constants';

export type SeededLayoutContent = {
  canvas: LayoutCanvas;
  objects: LayoutObject[];
};

// Editor state of a freshly loaded layout — the same values seed the state and
// the isDirty/reset baseline, so loading never marks anything as changed.
export const seedLayoutContent = (
  layout: Layout,
  objectForm?: LayoutInlineForm,
  canvasForm?: LayoutInlineForm
): SeededLayoutContent => {
  // Empty canvas fields fill in order: definition defaults (canvasForm),
  // then the FE fallbacks, then the sanitization.
  const canvasWithDefaults = applyLayoutObjectDefaults(
    layout.canvas as Record<string, unknown> | undefined,
    canvasForm
  ) as LayoutCanvas;
  // Fixed layouts come without canvas dimensions (their grid fills the
  // workspace — EditorBody sizes it) — seed FE-only fallbacks so the rest of
  // the editor never meets an undefined size. Never sent to the API.
  const missingCanvasFallback = {
    ...(canvasWithDefaults.width === undefined && {
      width: LAYOUT_FIXED_CANVAS_WIDTH,
    }),
    ...(canvasWithDefaults.height === undefined && {
      height: LAYOUT_FIXED_CANVAS_HEIGHT,
    }),
    ...(layout.canvas === undefined && { bestOfScale: true }),
  };

  return {
    canvas: {
      ...LAYOUT_CANVAS_DEFAULTS,
      ...missingCanvasFallback,
      ...canvasWithDefaults,
      ...sanitizeCanvasPatch(canvasWithDefaults, canvasWithDefaults),
    },
    // Control defaults go into the data AND the baseline — loading must not
    // mark anything as changed. feKey = FE-only identity (React key of the
    // panel form); it goes into the baseline too so it never counts as dirty.
    objects: (layout.objects ?? []).map((object) => ({
      ...applyLayoutObjectDefaults(object, objectForm),
      feKey: createLayoutObjectKey(),
    })),
  };
};
